import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Container,
  Grid,
  Stack,
  Typography,
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

// Icons
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import AssignmentIndOutlinedIcon from '@mui/icons-material/AssignmentIndOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import MailOutlineOutlinedIcon from '@mui/icons-material/MailOutlineOutlined';
import WorkOutlineOutlinedIcon from '@mui/icons-material/WorkOutlineOutlined';
import CurrencyRupeeOutlinedIcon from '@mui/icons-material/CurrencyRupeeOutlined';
import LocationCityOutlinedIcon from '@mui/icons-material/LocationCityOutlined';
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import SourceOutlinedIcon from '@mui/icons-material/SourceOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import PhoneCallbackOutlinedIcon from '@mui/icons-material/PhoneCallbackOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';

// Components
import BrudCrumbs from '../../components/BreadCrumbs';
import Page from '../../components/Page';
import Scrollbar from '../../components/Scrollbar';
import { fDate } from '../../utils/formatTime';
import { NOT_AVAILABLE } from '../Global/Constants';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import useCallCenterSelector from '../../store/Slice/CallCenterList/CallCenterListSelector';
import {
  getAllEmpMasterList,
  getCityData,
  getLoanTypeData,
  getStateData,
  setCityData
} from '../../store/Slice/Global/GlobalSlice';
import useGlobalSelector from '../../store/Slice/Global/GlobalSelector';
import { getCallCenterView } from '../../store/Slice/CallCenterList/CallCenterListSlice';
import CustomInput from '../../components/CustomInput';
import CustomSelect from '../../components/CustomSelect';
import CustomDialog from '../../components/CustomDialogue';
import { addProspectData } from '../../store/Slice/Prospect/ProspectSlice';

const getFieldIcon = (label) => {
  const l = (label || '').toLowerCase();
  if (l.includes('name') && !l.includes('campaign')) {
    return <PersonOutlineOutlinedIcon sx={{ fontSize: 22 }} />;
  }
  if (l.includes('phone') || l.includes('contact') || l.includes('mobile')) {
    return <PhoneOutlinedIcon sx={{ fontSize: 22 }} />;
  }
  if (l.includes('loan type') || l.includes('stage')) {
    return <GridViewOutlinedIcon sx={{ fontSize: 22 }} />;
  }
  if (l.includes('employment')) {
    return <WorkOutlineOutlinedIcon sx={{ fontSize: 22 }} />;
  }
  if (l.includes('amount')) {
    return <CurrencyRupeeOutlinedIcon sx={{ fontSize: 22 }} />;
  }
  if (l.includes('city')) {
    return <LocationCityOutlinedIcon sx={{ fontSize: 22 }} />;
  }
  if (l.includes('email')) {
    return <MailOutlineOutlinedIcon sx={{ fontSize: 22 }} />;
  }
  if (l.includes('campaign') || l.includes('source')) {
    return l.includes('name') ? (
      <CampaignOutlinedIcon sx={{ fontSize: 22 }} />
    ) : (
      <SourceOutlinedIcon sx={{ fontSize: 22 }} />
    );
  }
  if (l.includes('date') || l.includes('assigned') || l.includes('update')) {
    return <CalendarTodayOutlinedIcon sx={{ fontSize: 22 }} />;
  }
  if (l.includes('count')) {
    return <GroupsOutlinedIcon sx={{ fontSize: 22 }} />;
  }
  if (l.includes('id') || l.includes('prospect')) {
    return <DescriptionOutlinedIcon sx={{ fontSize: 22 }} />;
  }
  return <InsertDriveFileOutlinedIcon sx={{ fontSize: 22 }} />;
};

const getIconBoxStyle = (label) => {
  const l = (label || '').toLowerCase();
  if (l.includes('phone') || l.includes('mobile') || l.includes('person') || l.includes('name')) {
    return { bgcolor: '#ecfeff', color: '#06b6d4' };
  }
  if (l.includes('amount')) {
    return { bgcolor: '#f0fdf4', color: '#10b981' };
  }
  if (l.includes('loan type') || l.includes('stage') || l.includes('count')) {
    return { bgcolor: '#f5f3ff', color: '#6366f1' };
  }
  if (l.includes('employment')) {
    return { bgcolor: '#ecfeff', color: '#0891b2' };
  }
  if (l.includes('city') || l.includes('source') || l.includes('id') || l.includes('campaign')) {
    return { bgcolor: '#f0f9ff', color: '#0284c7' };
  }
  if (l.includes('date') || l.includes('assigned') || l.includes('update')) {
    return { bgcolor: '#eff6ff', color: '#3b82f6' };
  }
  return { bgcolor: '#f0f9ff', color: '#0284c7' };
};

const getHeaderIcon = (title) => {
  const t = (title || '').toLowerCase();
  if (t.includes('application')) {
    return <AssignmentIndOutlinedIcon sx={{ fontSize: 20 }} />;
  }
  if (t.includes('loan')) {
    return <GridViewOutlinedIcon sx={{ fontSize: 20 }} />;
  }
  if (t.includes('call')) {
    return <PhoneCallbackOutlinedIcon sx={{ fontSize: 20 }} />;
  }
  return <InsertDriveFileOutlinedIcon sx={{ fontSize: 20 }} />;
};

function ViewCallCenter() {
  const [isEdit, setIsEdit] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { rowId } = location?.state || {};
  const { LoginData } = useLoginSelector();
  const { stateDrop, CityDrop, LoanTypeDrop, AllEmpMasterDrop } = useGlobalSelector();
  const { CallViewData: viewCallCenterData = {}, CallViewData } = useCallCenterSelector();

  const {
    handleSubmit: handleReduxSubmit,
    control,
    setValue,
    reset,
    formState: { errors }
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      customerName: '',
      mobile: '',
      customerEmail: '',
      loanAmountRequested: '',
      loanTypeId: '',
      misMonth: ''
    }
  });

  // call center view
  const getCallCenterViewApi = useCallback(() => {
    const finalObject = {
      leadId: rowId,
      userId: LoginData?.userId,
      companyId: LoginData?.companyId
    };
    dispatch(getCallCenterView(finalObject));
  }, [LoginData, rowId, dispatch]);

  // loan master
  const getLoanMasterApi = useCallback(() => {
    const obj = {
      companyId: LoginData?.companyId
    };
    dispatch(getLoanTypeData(obj));
  }, [LoginData, dispatch]);

  // all employee master list
  const getEmployeeMasterApi = useCallback(() => {
    const obj = {
      companyId: LoginData?.companyId,
      userId: LoginData?.userId
    };
    dispatch(getAllEmpMasterList(obj));
  }, [LoginData, dispatch]);

  // state list
  const getStateList = () => {
    const data = {
      countryId: 101
    };
    dispatch(getStateData(data));
  };

  // show edit card
  const handleEditCard = () => {
    const editData = CallViewData?.editProspectDetails || {};
    reset();
    getLoanMasterApi();
    getStateList();
    getEmployeeMasterApi();
    handleStateChange(editData?.stateId);
    setIsEdit(true);

    setValue('customerName', editData?.name || '');
    setValue('customerEmail', editData?.email || '');
    setValue('mobile', editData?.phone || '');
    setValue('loanAmountRequested', editData?.loanAmount || '');
    setValue('provinceId', editData?.stateId);
    setValue('districtId', editData?.cityId);
    setValue('loanType', editData?.loanId);
    setValue('applicantEmploymentTypeId', editData?.employmentTypeId);
  };

  // close edit card
  const handleCloseCard = () => {
    setIsEdit(false);
  };

  // state change
  const handleStateChange = (optionVal) => {
    if (!optionVal) {
      dispatch(setCityData([]));
      setValue('districtId', null, { shouldValidate: true });
      return;
    }
    const data = {
      stateId: optionVal
    };
    dispatch(getCityData(data));
  };

  // edit submit api
  const onSubmit = useCallback(
    async (data) => {
      const finalData = {
        ...data,
        leadId: rowId,
        companyId: LoginData?.companyId,
        userId: LoginData?.userId
      };

      try {
        const res = await dispatch(addProspectData(finalData));
        if (res?.status === 'success') {
          getCallCenterViewApi();
          handleCloseCard();
        }
      } catch (error) {
        console.error('Error submitting form:', error);
      }
    },
    [LoginData, rowId, dispatch, getCallCenterViewApi]
  );

  useEffect(() => {
    getCallCenterViewApi();
  }, [getCallCenterViewApi]);

  const applicationDetailsFields = useMemo(
    () => [
      {
        label: 'Prospect ID',
        value: viewCallCenterData?.applicationDetails?.prospectId || NOT_AVAILABLE
      },
      {
        label: 'Campaign Source',
        value: viewCallCenterData?.applicationDetails?.campaignSource || NOT_AVAILABLE
      },
      {
        label: 'Campaign Name',
        value: viewCallCenterData?.applicationDetails?.campaignName || NOT_AVAILABLE
      },
      {
        label: 'Prospect Date',
        value: viewCallCenterData?.applicationDetails?.prospectDate
          ? fDate(viewCallCenterData.applicationDetails.prospectDate)
          : NOT_AVAILABLE
      },
      {
        label: 'Prospect Count',
        value: viewCallCenterData?.applicationDetails?.prospectCount || NOT_AVAILABLE
      },
      {
        label: 'Assigned On',
        value: viewCallCenterData?.applicationDetails?.assignedOn
          ? fDate(viewCallCenterData.applicationDetails.assignedOn)
          : NOT_AVAILABLE
      },
      {
        label: 'Current Stage',
        value: viewCallCenterData?.applicationDetails?.currentStage || NOT_AVAILABLE
      },
      {
        label: 'Last Update Date',
        value: viewCallCenterData?.applicationDetails?.lastUpdateDate
          ? fDate(viewCallCenterData.applicationDetails.lastUpdateDate)
          : NOT_AVAILABLE
      }
    ],
    [viewCallCenterData]
  );

  const loanRequirementFields = useMemo(
    () => [
      {
        label: 'Name',
        value: viewCallCenterData?.loanRequirement?.name || NOT_AVAILABLE
      },
      {
        label: 'Phone',
        value: viewCallCenterData?.loanRequirement?.phone || NOT_AVAILABLE
      },
      {
        label: 'Loan Type',
        value: viewCallCenterData?.loanRequirement?.loanType || NOT_AVAILABLE
      },
      {
        label: 'Employment Type',
        value: viewCallCenterData?.editProspectDetails?.employmentType || NOT_AVAILABLE
      },
      {
        label: 'Loan Amount',
        value: viewCallCenterData?.loanRequirement?.loanAmount
          ? Number(viewCallCenterData.loanRequirement.loanAmount)
          : NOT_AVAILABLE
      },
      {
        label: 'City',
        value: viewCallCenterData?.loanRequirement?.city || NOT_AVAILABLE
      },
      {
        label: 'Email',
        value: viewCallCenterData?.loanRequirement?.email || NOT_AVAILABLE
      }
    ],
    [viewCallCenterData]
  );

  const currentStatus = viewCallCenterData?.applicationDetails?.currentStatus || NOT_AVAILABLE;

  return (
    <Page title="Partner Management System || Call Center View">
      <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh', pb: 8, pt: 2.5 }}>
        <Container maxWidth="xl">
          {/* Top Header Row */}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            justifyContent="space-between"
            sx={{ mb: 3.5, gap: 2 }}
          >
            {/* Left: Back Button & Title */}
            <Stack direction="row" spacing={2.5} alignItems="center">
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon sx={{ fontSize: 18 }} />}
                onClick={() => navigate('/call-center')}
                sx={{
                  color: '#0284c7',
                  borderColor: '#bae6fd',
                  bgcolor: '#ffffff',
                  borderRadius: '10px',
                  fontWeight: 600,
                  fontSize: '13px',
                  textTransform: 'none',
                  py: 0.9,
                  px: 2,
                  boxShadow: '0 2px 6px rgba(2, 132, 199, 0.06)',
                  '&:hover': {
                    bgcolor: '#f0f9ff',
                    borderColor: '#0284c7'
                  }
                }}
              >
                Back to Call Center List
              </Button>

              <Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 800,
                    color: '#0f172a',
                    fontSize: { xs: '20px', sm: '24px' },
                    letterSpacing: '-0.3px',
                    lineHeight: 1.2
                  }}
                >
                  Call Center View
                </Typography>
                <Typography variant="body2" sx={{ color: '#64748b', fontSize: '13px', mt: 0.25 }}>
                  View detailed prospect information, loan requirement, and call logs.
                </Typography>
              </Box>
            </Stack>

            {/* Right: Breadcrumbs & Current Status */}
            <Stack direction="row" spacing={2} alignItems="center">
              <Box>
                <BrudCrumbs />
              </Box>

              <Stack direction="row" spacing={1} alignItems="center">
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#0f172a' }}>
                  Current Status:
                </Typography>
                <Box
                  sx={{
                    bgcolor: '#fef3c7',
                    color: '#b45309',
                    px: 1.75,
                    py: 0.4,
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: 700,
                    letterSpacing: '0.2px',
                    border: '1px solid #fde68a'
                  }}
                >
                  {currentStatus}
                </Box>
              </Stack>
            </Stack>
          </Stack>

          {/* Dynamic Section Cards */}
          <Scrollbar>
            <Stack spacing={3}>
              {/* Application Details Card */}
              <Card
                sx={{
                  borderRadius: '16px',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
                  p: { xs: 2.5, md: 3 },
                  bgcolor: '#ffffff'
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 3, pb: 2, borderBottom: '1px solid #f1f5f9' }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: '8px',
                        bgcolor: '#0284c7',
                        color: '#ffffff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      {getHeaderIcon('Application Details')}
                    </Box>
                    <Typography sx={{ fontSize: '16px', fontWeight: 800, color: '#0f172a' }}>
                      Application Details
                    </Typography>
                  </Stack>
                </Stack>

                <Grid container spacing={3}>
                  {applicationDetailsFields.map((field, idx) => {
                    const iconStyle = getIconBoxStyle(field.label);
                    return (
                      <Grid item xs={12} sm={6} md={3} key={idx}>
                        <Stack direction="row" spacing={1.75} alignItems="center">
                          <Box
                            sx={{
                              width: 44,
                              height: 44,
                              borderRadius: '12px',
                              bgcolor: iconStyle.bgcolor,
                              color: iconStyle.color,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0
                            }}
                          >
                            {getFieldIcon(field.label)}
                          </Box>
                          <Box>
                            <Typography
                              sx={{ fontSize: '11.5px', color: '#64748b', fontWeight: 500 }}
                            >
                              {field.label}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: '14px',
                                fontWeight: 700,
                                color: '#0f172a',
                                mt: 0.2
                              }}
                            >
                              {field.value}
                            </Typography>
                          </Box>
                        </Stack>
                      </Grid>
                    );
                  })}
                </Grid>
              </Card>

              {/* Loan Requirement Card */}
              <Card
                sx={{
                  borderRadius: '16px',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
                  p: { xs: 2.5, md: 3 },
                  bgcolor: '#ffffff'
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 3, pb: 2, borderBottom: '1px solid #f1f5f9' }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: '8px',
                        bgcolor: '#0284c7',
                        color: '#ffffff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      {getHeaderIcon('Loan Requirement')}
                    </Box>
                    <Typography sx={{ fontSize: '16px', fontWeight: 800, color: '#0f172a' }}>
                      Loan Requirement
                    </Typography>
                  </Stack>

                  <Button
                    variant="outlined"
                    startIcon={<EditOutlinedIcon sx={{ fontSize: 16 }} />}
                    onClick={handleEditCard}
                    sx={{
                      color: '#0284c7',
                      borderColor: '#bae6fd',
                      bgcolor: '#f0f9ff',
                      borderRadius: '8px',
                      fontWeight: 600,
                      fontSize: '12.5px',
                      textTransform: 'none',
                      py: 0.6,
                      px: 1.75,
                      '&:hover': {
                        bgcolor: '#e0f2fe',
                        borderColor: '#0284c7'
                      }
                    }}
                  >
                    Edit
                  </Button>
                </Stack>

                <Grid container spacing={3}>
                  {loanRequirementFields.map((field, idx) => {
                    const iconStyle = getIconBoxStyle(field.label);
                    return (
                      <Grid item xs={12} sm={6} md={3} key={idx}>
                        <Stack direction="row" spacing={1.75} alignItems="center">
                          <Box
                            sx={{
                              width: 44,
                              height: 44,
                              borderRadius: '12px',
                              bgcolor: iconStyle.bgcolor,
                              color: iconStyle.color,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0
                            }}
                          >
                            {getFieldIcon(field.label)}
                          </Box>
                          <Box>
                            <Typography
                              sx={{ fontSize: '11.5px', color: '#64748b', fontWeight: 500 }}
                            >
                              {field.label}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: '14px',
                                fontWeight: 700,
                                color: '#0f172a',
                                mt: 0.2
                              }}
                            >
                              {field.value}
                            </Typography>
                          </Box>
                        </Stack>
                      </Grid>
                    );
                  })}
                </Grid>
              </Card>

              {/* Call Log Card */}
              <Card
                sx={{
                  borderRadius: '16px',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
                  p: { xs: 2.5, md: 3 },
                  bgcolor: '#ffffff'
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 3, pb: 2, borderBottom: '1px solid #f1f5f9' }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: '8px',
                        bgcolor: '#0284c7',
                        color: '#ffffff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      {getHeaderIcon('Call Log')}
                    </Box>
                    <Typography sx={{ fontSize: '16px', fontWeight: 800, color: '#0f172a' }}>
                      Call Log
                    </Typography>
                  </Stack>
                </Stack>

                <TableContainer
                  sx={{
                    border: '1px solid #e2e8f0',
                    borderRadius: '12px',
                    overflow: 'hidden'
                  }}
                >
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f8fafc' }}>
                        <TableCell
                          sx={{
                            color: '#64748b',
                            fontSize: '11.5px',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            py: 1.5,
                            borderBottom: '1px solid #e2e8f0'
                          }}
                        >
                          Call Date
                        </TableCell>
                        <TableCell
                          sx={{
                            color: '#64748b',
                            fontSize: '11.5px',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            py: 1.5,
                            borderBottom: '1px solid #e2e8f0'
                          }}
                        >
                          Call Agent
                        </TableCell>
                        <TableCell
                          sx={{
                            color: '#64748b',
                            fontSize: '11.5px',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            py: 1.5,
                            borderBottom: '1px solid #e2e8f0'
                          }}
                        >
                          Status
                        </TableCell>
                        <TableCell
                          sx={{
                            color: '#64748b',
                            fontSize: '11.5px',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            py: 1.5,
                            borderBottom: '1px solid #e2e8f0'
                          }}
                        >
                          Comments
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {viewCallCenterData?.callLog && viewCallCenterData.callLog.length > 0 ? (
                        viewCallCenterData.callLog.map((log, index) => (
                          <TableRow key={index} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                            <TableCell
                              sx={{
                                fontSize: '13px',
                                color: '#334155',
                                py: 1.5,
                                borderBottom: '1px solid #f1f5f9'
                              }}
                            >
                              {log.callDateTime ? fDate(log.callDateTime) : NOT_AVAILABLE}
                            </TableCell>
                            <TableCell
                              sx={{
                                fontSize: '13px',
                                color: '#0f172a',
                                fontWeight: 600,
                                py: 1.5,
                                borderBottom: '1px solid #f1f5f9'
                              }}
                            >
                              {log.callAgentName || NOT_AVAILABLE}
                            </TableCell>
                            <TableCell
                              sx={{
                                py: 1.5,
                                borderBottom: '1px solid #f1f5f9'
                              }}
                            >
                              <Box
                                component="span"
                                sx={{
                                  bgcolor: '#f0fdf4',
                                  color: '#16a34a',
                                  px: 1.5,
                                  py: 0.3,
                                  borderRadius: '20px',
                                  fontSize: '12px',
                                  fontWeight: 600,
                                  display: 'inline-block'
                                }}
                              >
                                {log.callStatus || NOT_AVAILABLE}
                              </Box>
                            </TableCell>
                            <TableCell
                              sx={{
                                fontSize: '13px',
                                color: '#64748b',
                                py: 1.5,
                                borderBottom: '1px solid #f1f5f9'
                              }}
                            >
                              {log.callComments || NOT_AVAILABLE}
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell
                            colSpan={4}
                            align="center"
                            sx={{ py: 3, color: '#94a3b8', fontSize: '13px' }}
                          >
                            No call logs available
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Card>
            </Stack>
          </Scrollbar>
        </Container>

        {/* Edit Dialog */}
        <CustomDialog
          showCloseIcon
          open={isEdit}
          onClose={(event, reason) => {
            if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
              handleCloseCard();
            }
          }}
          title="Edit Loan Requirement"
          content={
            <Box mb={3}>
              <form id="EditLoanRequirementForm" onSubmit={handleReduxSubmit(onSubmit)}>
                <Grid container spacing={3} mb={3}>
                  <Grid item xs={6}>
                    <CustomInput
                      name="customerName"
                      label="Name"
                      control={control}
                      errors={errors}
                      isRequired
                      fieldCode="NAME"
                      fieldType="ALPHABET"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <CustomInput
                      name="mobile"
                      label="Mobile"
                      control={control}
                      errors={errors}
                      isRequired
                      fieldCode="MOBILE"
                      fieldType="NUMBER_FIELD"
                      type="Number"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <CustomInput
                      name="customerEmail"
                      label="Email"
                      control={control}
                      errors={errors}
                      isRequired
                      fieldCode="EMAIL"
                      fieldType="TEXT_FIELD"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <CustomSelect
                      name="provinceId"
                      label="State"
                      control={control}
                      errors={errors}
                      options={stateDrop || []}
                      isRequired
                      onChange={(event, val) => {
                        handleStateChange(val);
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <CustomSelect
                      name="districtId"
                      label="City"
                      control={control}
                      errors={errors}
                      options={CityDrop || []}
                      isRequired
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <CustomSelect
                      name="loanType"
                      label="Loan Type"
                      control={control}
                      errors={errors}
                      options={LoanTypeDrop || []}
                      isRequired
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <CustomSelect
                      label="Employment Type"
                      name="applicantEmploymentTypeId"
                      control={control}
                      errors={errors}
                      options={AllEmpMasterDrop || []}
                      isRequired
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <CustomInput
                      name="loanAmountRequested"
                      label="Loan Amount"
                      type="Number"
                      control={control}
                      errors={errors}
                      isRequired
                      fieldType="AMOUNT_FIELD"
                    />
                  </Grid>
                </Grid>
              </form>
            </Box>
          }
          actions={
            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button
                variant="outlined"
                sx={{
                  borderRadius: '8px',
                  textTransform: 'none',
                  borderColor: '#e2e8f0',
                  color: '#64748b',
                  '&:hover': { borderColor: '#cbd5e1', bgcolor: '#f8fafc' }
                }}
                onClick={handleCloseCard}
              >
                Cancel
              </Button>

              <Button
                type="submit"
                form="EditLoanRequirementForm"
                variant="contained"
                sx={{
                  bgcolor: '#0284c7',
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  boxShadow: '0 4px 14px rgba(2, 132, 199, 0.25)',
                  '&:hover': { bgcolor: '#0369a1' }
                }}
              >
                Save
              </Button>
            </Box>
          }
        />
      </Box>
    </Page>
  );
}

export default ViewCallCenter;
